import React, { useState } from 'react';
import { Box, TextField, Select, MenuItem, FormControl, 
         InputLabel, FormHelperText, Button, Typography, Alert } from '@mui/material';
import styled from '@emotion/styled';
import axios from 'axios';

const FormContainer = styled(Box)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormActions = styled(Box)`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
`;

const FormComponent = ({ questions, onSubmit, onSkip, sessionToken, sessionSignature, onAuthFailure }) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questionsList = questions.questions;
  const currentQuestion = questionsList[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === questionsList.length - 1;
  const canSkip = !currentQuestion.validation?.required;

  const validateField = (question, value) => {
    if (!question.validation) return null;
    
    const { required, pattern, min, max, minLength, maxLength } = question.validation;
    
    if (required && (!value || value === '')) {
      return question.validation.errorMessage || 'This field is required';
    }
    
    if (pattern && value && !new RegExp(pattern).test(value)) {
      return question.validation.errorMessage || 'Invalid format';
    }
    
    if (typeof value === 'number') {
      if (min !== undefined && value < min) return `Minimum value is ${min}`;
      if (max !== undefined && value > max) return `Maximum value is ${max}`;
    }
    
    if (typeof value === 'string') {
      if (minLength && value.length < minLength) return `Minimum length is ${minLength}`;
      if (maxLength && value.length > maxLength) return `Maximum length is ${maxLength}`;
    }
    
    return null;
  };

  const handleChange = (id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }));
    const error = validateField(currentQuestion, value);
    setErrors(prev => ({ ...prev, [id]: error }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (!sessionToken || !sessionSignature) {
        if (onAuthFailure) {
          onAuthFailure({ status: 401, data: { code: 'SESSION_EXPIRED' } });
        }
        setErrors(prev => ({
          ...prev,
          submit: 'Payment session expired. Please refresh the payment page.'
        }));
        return;
      }
      // Format the form data into the required structure
      const responses = Object.entries(formData).map(([id, response]) => ({
        question_id: id,
        response: response
      }));

      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/iframe/submit-form`,
        { responses },
        {
          headers: {
            'X-Session-Token': sessionToken,
            'X-Signature': sessionSignature
          }
        }
      );
      onSubmit(formData);
    } catch (error) {
      if (onAuthFailure && onAuthFailure(error.response)) {
        return;
      }
      console.error('Error submitting form:', error);
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to submit form. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    const error = validateField(currentQuestion, formData[currentQuestion.id]);
    if (!error) {
      if (isLastQuestion) {
        handleSubmit();
      } else {
        setCurrentQuestionIndex(prev => prev + 1);
      }
    } else {
      setErrors(prev => ({ ...prev, [currentQuestion.id]: error }));
    }
  };

  const handleSkip = () => {
    if (isLastQuestion) {
      handleSubmit();
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const renderField = (question) => {
    switch (question.type) {
      case 'multiple_choice':
        return (
          <FormControl fullWidth error={!!errors[question.id]}>
            <InputLabel>{question.question}</InputLabel>
            <Select
              value={formData[question.id] || ''}
              onChange={(e) => handleChange(question.id, e.target.value)}
              label={question.question}
            >
              {question.options.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
            {errors[question.id] && (
              <FormHelperText>{errors[question.id]}</FormHelperText>
            )}
          </FormControl>
        );

      case 'email':
        return (
          <TextField
            fullWidth
            type="email"
            label={question.question}
            value={formData[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            error={!!errors[question.id]}
            helperText={errors[question.id]}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            type="number"
            label={question.question}
            value={formData[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            error={!!errors[question.id]}
            helperText={errors[question.id]}
          />
        );

      default:
        return (
          <TextField
            fullWidth
            label={question.question}
            value={formData[question.id] || ''}
            onChange={(e) => handleChange(question.id, e.target.value)}
            error={!!errors[question.id]}
            helperText={errors[question.id]}
          />
        );
    }
  };

  return (
    <FormContainer>
      <Typography variant="h6" gutterBottom>
        Question {currentQuestionIndex + 1} of {questionsList.length}
      </Typography>
      
      {errors.submit && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.submit}
        </Alert>
      )}

      <Box>
        {renderField(currentQuestion)}
      </Box>

      <FormActions>
        {canSkip && (
          <Button 
            onClick={handleSkip} 
            color="inherit"
            disabled={isSubmitting}
          >
            {isLastQuestion ? 'Skip & Submit' : 'Skip'}
          </Button>
        )}
        <Button 
          onClick={handleNext} 
          variant="contained" 
          color="primary"
          disabled={isSubmitting}
        >
          {isSubmitting 
            ? 'Submitting...' 
            : isLastQuestion 
              ? 'Submit' 
              : 'Next'
          }
        </Button>
      </FormActions>
    </FormContainer>
  );
};


export default FormComponent;
